import React, { useCallback, useEffect, useRef, useState } from 'react';
import Grid from '@decisiv/iconix/lib/components/Grid';
import { Badge, Flex, H3, H4, Loading } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';

import { isEmpty } from 'lodash';

import IntegrationContainer from '../../containers/IntegrationContainer/index';
import HomePageTitle from '../../components/PageTitle/HomePageTitle';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import { IntegrationsWrapper } from '../../containers/IntegrationContainer/IntegrationsWrapper.styled';
import Helmet from '../../components/SEO/Helmet';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import useHomeLayout from '../../hooks/useHomeLayout';
import IntegrationCard from './layouts/IntegrationCard';
import IntegrationsTable from './layouts/IntegrationsTable';
import SharedSettingsApi from '../../api/shared_settings';
import { useIntegrationsActions } from '../../contexts/IntegrationsActionsContext';
import IntegrationSelectionBar from '../../components/IntegrationSelectionBar';
import ExportIntegrationsModal from '../../components/ExportIntegrationsModal';
import ImportIntegrationsModal from '../../components/ImportIntegrationsModal';
import MAX_EXPORT_SIZE from '../../utils/maxExportSize';
import { isAfterNow, secondsFrom } from '../../utils/dates';

export default function IntegrationDetailPage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [allSharedSettings, setAllSharedSettings] = useState([]);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const [exportMode, setExportMode] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState(() => new Set());
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const { registerActions } = useIntegrationsActions();
  const { layout } = useHomeLayout();

  const appendIntegrations = useCallback((newItems) => {
    setIntegrations((prev) => [...prev, ...newItems]);
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (loadingMoreRef.current || !loadMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    await loadMoreRef.current.loadMore();
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }, []);

  const sentinelRef = useInfiniteScroll(handleLoadMore, hasMore);

  useEffect(() => {
    async function fetchSettings() {
      const { data } = await SharedSettingsApi.fetchAllSharedSettings();
      if (data) {
        setAllSharedSettings(
          data.map((item) => ({ id: item.id, ...item.attributes })),
        );
      }
    }
    fetchSettings();
  }, []);

  const displayCircuitbreaker = (integrationItem) => {
    if (
      !integrationItem.circuit_breaker_enabled ||
      isEmpty(integrationItem.circuit_breaker_opened_at)
    ) {
      return false;
    }

    const circuitBreakerEndDate = secondsFrom(
      integrationItem.circuit_breaker_opened_at,
      integrationItem.circuit_breaker_retry_after,
    );

    if (isAfterNow(circuitBreakerEndDate)) {
      return true;
    }

    return false;
  };

  const integrationsWithCircuitBreaker = React.useMemo(
    () =>
      integrations.map((item) => {
        const enabled = displayCircuitbreaker(item.attributes);

        return {
          ...item,
          attributes: {
            ...item.attributes,
            circuit_breaker_current_enabled: enabled,
          },
        };
      }),
    [integrations],
  );

  const toggleSelect = useCallback((uuid) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else if (next.size < MAX_EXPORT_SIZE) {
        next.add(uuid);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedUuids(new Set()), []);

  const exitExportMode = useCallback(() => {
    setExportMode(false);
    clearSelection();
  }, [clearSelection]);

  const refreshIntegrations = useCallback(
    () => setRefreshSignal((prev) => prev + 1),
    [],
  );

  // The Integrations menu lives in the global TopNav, above the router, so it
  // can't call this page's handlers directly — it reaches them through
  // IntegrationsActionsContext instead. Only register while mounted; other
  // routes leave `available: false` and the menu shows Create Integration only.
  useEffect(() => {
    registerActions({
      available: true,
      onImport: () => setImportModalOpen(true),
      onStartExport: () => setExportMode(true),
    });
    return () => registerActions({ available: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerActions]);

  const selectedIntegrations = integrationsWithCircuitBreaker.filter(({ id }) =>
    selectedUuids.has(id),
  );

  // Open circuit breakers first; otherwise the order the API returned.
  const sortedIntegrations = React.useMemo(
    () =>
      integrationsWithCircuitBreaker
        .map((item, originalIndex) => ({ ...item, originalIndex }))
        .sort((a, b) => {
          const aEnabled = a.attributes.circuit_breaker_current_enabled;
          const bEnabled = b.attributes.circuit_breaker_current_enabled;

          if (aEnabled && !bEnabled) return -1;
          if (!aEnabled && bEnabled) return 1;

          return a.originalIndex - b.originalIndex;
        }),
    [integrationsWithCircuitBreaker],
  );

  const selectAllShown = useCallback(() => {
    setSelectedUuids(
      new Set(sortedIntegrations.slice(0, MAX_EXPORT_SIZE).map(({ id }) => id)),
    );
  }, [sortedIntegrations]);

  return (
    <>
      <HomePageTitle
        setLoading={setLoading}
        setIntegrations={setIntegrations}
        appendIntegrations={appendIntegrations}
        setHasMore={setHasMore}
        loadMoreRef={loadMoreRef}
        refreshSignal={refreshSignal}
        exportMode={exportMode}
        onSelectAllShown={selectAllShown}
        shownCount={integrationsWithCircuitBreaker.length}
      />
      <Helmet title={t`Integrations`} />
      <WrapperWithLoading loading={loading}>
        {integrations.length > 0 ? (
          <>
            {layout === 'dense-list' ? (
              <IntegrationsTable
                integrations={sortedIntegrations}
                selectable={exportMode}
                selectedUuids={selectedUuids}
                selectionLimitReached={selectedUuids.size >= MAX_EXPORT_SIZE}
                onToggleSelect={toggleSelect}
              />
            ) : (
              <IntegrationsWrapper title="integrations-container">
                {sortedIntegrations.map(({ id, attributes }) => {
                  const cardProps = {
                    integrationId: id,
                    locationUrl: attributes.slug ?? id,
                    integration: attributes,
                    allSharedSettings,
                    selectable: exportMode,
                    selected: selectedUuids.has(id),
                    selectionLimitReached:
                      selectedUuids.size >= MAX_EXPORT_SIZE,
                    onToggleSelect: () => toggleSelect(id),
                  };

                  return layout === 'classic' ? (
                    <IntegrationContainer key={id} {...cardProps} />
                  ) : (
                    <IntegrationCard key={id} {...cardProps} />
                  );
                })}
              </IntegrationsWrapper>
            )}
            {loadingMore && (
              <Flex justifyContent="center" padding={1}>
                <Loading size="small" />
              </Flex>
            )}
            {hasMore && !loadingMore && (
              <div ref={sentinelRef} style={{ height: '1px' }} />
            )}
          </>
        ) : (
          <>
            <Flex
              justifyContent="center"
              style={{
                width: '100%',
                backgroundColor: 'var(--if-surface-1)',
              }}
            >
              <Flex marginTop={3} flexDirection="column">
                <Flex alignSelf="center">
                  <Badge
                    key="key"
                    aria-label="an example badge"
                    color="licoriceMousse"
                    icon={Grid}
                    size="large"
                    palette="bright"
                  />
                </Flex>
                <Flex alignSelf="center">
                  <H3>
                    <Trans>No Integrations Found</Trans>
                  </H3>
                </Flex>
                <Flex alignSelf="center">
                  <H4 color="alaskanHusky">
                    <Trans>
                      Try removing or editing some of your filters or search
                      criteria to expand your search.
                    </Trans>
                  </H4>
                </Flex>
              </Flex>
            </Flex>
            <Flex
              justifyContent="center"
              style={{
                width: '100%',
                height: '100vh',
                backgroundColor: 'var(--if-surface-1)',
              }}
            />
          </>
        )}
      </WrapperWithLoading>
      {exportMode && (
        <IntegrationSelectionBar
          count={selectedUuids.size}
          max={MAX_EXPORT_SIZE}
          onExport={() => setExportModalOpen(true)}
          onCancel={exitExportMode}
        />
      )}
      <ExportIntegrationsModal
        visible={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        integrations={selectedIntegrations}
        onRemove={toggleSelect}
        onExported={() => {
          setExportModalOpen(false);
          exitExportMode();
        }}
      />
      <ImportIntegrationsModal
        visible={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={refreshIntegrations}
      />
    </>
  );
}
